import { useState, useRef, useEffect } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useClassroom } from '../contexts/ClassroomContext';
import { useLanguage } from '../contexts/LanguageContext';
import { MuteButton } from './MuteButton';
import {
  LayoutDashboard, Gamepad2, LogOut,
  ChevronLeft, ChevronRight, GraduationCap, BarChart3, Settings, BookOpen,
  ChevronDown, FlaskConical, School, Plus, Pencil, Trash2, Check, X, Globe, Shield, Compass, HelpCircle
} from 'lucide-react';

const NAV_KEYS = [
  { to: '/dashboard', icon: LayoutDashboard, labelKey: 'nav.dashboard' },
  { to: '/browse', icon: Gamepad2, labelKey: 'nav.browse' },
  { to: '/progress', icon: BarChart3, labelKey: 'nav.progress', authOnly: true },
  { to: '/programs', icon: GraduationCap, labelKey: 'nav.programs', authOnly: true, requiresProgramsEnabled: true },
  { to: '/guide', icon: BookOpen, labelKey: 'nav.guide', authOnly: true },
  { to: '/faq', icon: HelpCircle, labelKey: 'nav.faq', authOnly: true },
  { to: '/admin', icon: Shield, labelKey: 'nav.platformAdmin', authOnly: true, requiresPlatformAdmin: true },
];

export function TeacherLayout() {
  const [collapsed, setCollapsed] = useState(false);
  const { user, logout } = useAuth();
  const { classrooms, activeClassroom, switchClassroom, createClassroom, deleteClassroom } = useClassroom();
  const { lang, setLang, t } = useLanguage();
  const navigate = useNavigate();
  const sidebarWidth = collapsed ? 72 : 240;
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const [showNewForm, setShowNewForm] = useState(false);
  const [newName, setNewName] = useState('');
  const switcherRef = useRef(null);
  const isGuest = user?.role === 'invite_lead';
  const isFullDemo = !!user?.demo || isGuest; // hide chrome from URL-demo + invite_lead

  // Close switcher on click outside
  useEffect(() => {
    const handler = (e) => {
      if (switcherRef.current && !switcherRef.current.contains(e.target)) {
        setSwitcherOpen(false);
        setShowNewForm(false);
        setNewName('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  const handleCreateClassroom = async () => {
    if (!newName.trim()) return;
    try {
      const created = await createClassroom(newName.trim());
      switchClassroom(created);
      setNewName('');
      setShowNewForm(false);
      setSwitcherOpen(false);
    } catch { /* ignore */ }
  };

  const handleDeleteClassroom = async (e, c) => {
    e.stopPropagation();
    if (c.type === 'sandbox') return;
    await deleteClassroom(c.id);
  };

  const isSandbox = activeClassroom?.type === 'sandbox';

  return (
    <div style={{ display: 'flex', height: '100%' }}>
      {/* Sidebar */}
      <aside
        data-testid="teacher-sidebar"
        style={{
          width: sidebarWidth, minWidth: sidebarWidth, height: '100%',
          background: 'white', borderRight: '1px solid #E2E8F0',
          display: 'flex', flexDirection: 'column',
          transition: 'width 0.25s ease, min-width 0.25s ease',
          overflow: 'hidden', zIndex: 10, flexShrink: 0,
        }}
      >
        {/* Logo */}
        <div style={{
          padding: collapsed ? '1.25rem 0' : '1.25rem 1.25rem',
          display: 'flex', alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'flex-start',
          gap: '0.75rem', borderBottom: '1px solid #F1F5F9', minHeight: 72,
        }}>
          <div style={{
            width: 36, height: 36, borderRadius: '0.625rem',
            background: '#1E2140', display: 'flex',
            alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <GraduationCap size={20} color="white" />
          </div>
          {!collapsed && (
            <span style={{
              fontFamily: 'Outfit, sans-serif', fontWeight: 700, fontSize: '1.05rem',
              color: '#1A1D20', whiteSpace: 'nowrap',
            }}>
              FrenchBoard
            </span>
          )}
        </div>

        {/* Classroom Switcher */}
        {activeClassroom && (
          <div ref={switcherRef} style={{ padding: collapsed ? '0.5rem 0.25rem' : '0.5rem 0.75rem', position: 'relative' }}>
            <button
              data-testid="classroom-switcher-btn"
              onClick={() => { if (!collapsed) setSwitcherOpen(!switcherOpen); }}
              style={{
                display: 'flex', alignItems: 'center', gap: '0.5rem',
                width: '100%', padding: collapsed ? '0.5rem' : '0.5rem 0.625rem',
                borderRadius: '0.5rem', border: '1.5px solid #E2E8F0',
                background: '#F1F5F9',
                cursor: 'pointer', transition: 'all 0.15s',
                justifyContent: collapsed ? 'center' : 'flex-start',
              }}
              title={collapsed ? activeClassroom.name : undefined}
            >
              {isSandbox
                ? <FlaskConical size={16} color="#64748B" style={{ flexShrink: 0 }} />
                : <School size={16} color="#1E2140" style={{ flexShrink: 0 }} />
              }
              {!collapsed && (
                <>
                  <span style={{
                    flex: 1, fontFamily: 'Nunito, sans-serif', fontSize: '0.8rem',
                    fontWeight: 700, color: '#1A1D20',
                    textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {activeClassroom.name}
                  </span>
                  <ChevronDown
                    size={14}
                    color="#94A3B8"
                    style={{ flexShrink: 0, transform: switcherOpen ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.2s' }}
                  />
                </>
              )}
            </button>

            {/* Dropdown */}
            {switcherOpen && !collapsed && (
              <div
                data-testid="classroom-switcher-dropdown"
                style={{
                  position: 'absolute', top: '100%', left: '0.75rem', right: '0.75rem',
                  background: 'white', borderRadius: '0.625rem',
                  border: '1px solid #E2E8F0', boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                  zIndex: 50, marginTop: '0.25rem', maxHeight: 320, overflow: 'auto',
                }}
              >
                {classrooms.map(c => {
                  const isActive = c.id === activeClassroom?.id;
                  const isSb = c.type === 'sandbox';
                  return (
                    <div
                      key={c.id}
                      data-testid={`classroom-option-${c.id}`}
                      onClick={() => { switchClassroom(c); setSwitcherOpen(false); }}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '0.5rem',
                        padding: '0.625rem 0.75rem', cursor: 'pointer',
                        background: isActive ? '#F1F5F9' : 'transparent',
                        transition: 'background 0.1s',
                        borderBottom: '1px solid #F8F9FB',
                      }}
                      onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = '#F1F5F9'; }}
                      onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
                    >
                      {isSb
                        ? <FlaskConical size={14} color="#64748B" style={{ flexShrink: 0 }} />
                        : <School size={14} color="#1E2140" style={{ flexShrink: 0 }} />
                      }
                      <span style={{
                        flex: 1, fontFamily: 'Nunito, sans-serif', fontSize: '0.8rem',
                        fontWeight: isActive ? 700 : 500, color: '#1A1D20',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>
                        {c.name}
                      </span>
                      {isActive && <Check size={14} color="#1E2140" strokeWidth={3} style={{ flexShrink: 0 }} />}
                      {!isSb && !isActive && (
                        <button
                          onClick={(e) => handleDeleteClassroom(e, c)}
                          data-testid={`delete-classroom-${c.id}`}
                          style={{
                            background: 'none', border: 'none', cursor: 'pointer',
                            color: '#CBD5E1', padding: '2px', display: 'flex',
                            alignItems: 'center', flexShrink: 0,
                          }}
                          onMouseEnter={e => { e.currentTarget.style.color = '#E03131'; }}
                          onMouseLeave={e => { e.currentTarget.style.color = '#CBD5E1'; }}
                        >
                          <Trash2 size={13} />
                        </button>
                      )}
                    </div>
                  );
                })}

                {/* New classroom form */}
                <div style={{ padding: '0.5rem 0.75rem', borderTop: '1px solid #F1F5F9' }}>
                  {showNewForm ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                      <input
                        data-testid="new-classroom-input"
                        value={newName}
                        onChange={e => setNewName(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && handleCreateClassroom()}
                        placeholder="e.g., Grade 4A, Tuesday Batch, Beginner Group"
                        autoFocus
                        style={{
                          flex: 1, fontFamily: 'Nunito, sans-serif', fontSize: '0.775rem',
                          border: '1px solid #E2E8F0', borderRadius: '0.375rem',
                          padding: '0.375rem 0.5rem', outline: 'none', color: '#1A1D20',
                          minWidth: 0,
                        }}
                      />
                      <button
                        data-testid="new-classroom-save"
                        onClick={handleCreateClassroom}
                        style={{
                          background: '#1E2140', color: 'white', border: 'none',
                          borderRadius: '0.375rem', padding: '0.375rem',
                          cursor: 'pointer', display: 'flex', alignItems: 'center',
                        }}
                      >
                        <Check size={14} />
                      </button>
                      <button
                        onClick={() => { setShowNewForm(false); setNewName(''); }}
                        style={{
                          background: 'transparent', color: '#94A3B8', border: '1px solid #E2E8F0',
                          borderRadius: '0.375rem', padding: '0.375rem',
                          cursor: 'pointer', display: 'flex', alignItems: 'center',
                        }}
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ) : (
                    <button
                      data-testid="new-classroom-btn"
                      onClick={() => setShowNewForm(true)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '0.375rem',
                        background: 'none', border: 'none', cursor: 'pointer',
                        fontFamily: 'Nunito, sans-serif', fontSize: '0.8rem',
                        fontWeight: 600, color: '#64748B', padding: '0.25rem 0',
                        width: '100%',
                      }}
                    >
                      <Plus size={14} /> {t('nav.newGroup')}
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Nav */}
        <nav style={{ flex: 1, padding: '0.75rem', display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
          {NAV_KEYS.map(item => {
            if (item.authOnly && user?.demo) return null;
            if (item.requiresProgramsEnabled && !user?.programs_enabled) return null;
            if (item.requiresPlatformAdmin && user?.role !== 'admin') return null;
            const label = t(item.labelKey);
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/' || item.to === '/dashboard'}
                data-testid={`sidebar-nav-${label.toLowerCase().replace(/\s/g, '-')}`}
                style={({ isActive }) => ({
                  display: 'flex', alignItems: 'center', gap: '0.75rem',
                  padding: collapsed ? '0.75rem' : '0.75rem 1rem',
                  borderRadius: '0.625rem', textDecoration: 'none',
                  fontFamily: 'Nunito, sans-serif', fontSize: '0.925rem',
                  fontWeight: isActive ? 700 : 500,
                  color: isActive ? '#1E2140' : '#64748B',
                  background: isActive ? '#F1F5F9' : 'transparent',
                  justifyContent: collapsed ? 'center' : 'flex-start',
                  transition: 'all 0.15s ease', whiteSpace: 'nowrap',
                })}
              >
                <item.icon size={20} style={{ flexShrink: 0 }} />
                {!collapsed && label}
              </NavLink>
            );
          })}
        </nav>

        {/* Bottom */}
        <div style={{ padding: '0.75rem', borderTop: '1px solid #F1F5F9' }}>
          {!collapsed && user && !isFullDemo && (
            <div style={{
              padding: '0.75rem 1rem', marginBottom: '0.5rem',
              borderRadius: '0.625rem', background: '#F8F9FB',
            }}>
              <div style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.85rem', fontWeight: 600, color: '#1A1D20', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {user.name || user.email}
              </div>
              <div style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.75rem', color: '#94A3B8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {user.school_name || user.role}
              </div>
            </div>
          )}

          {/* Demo footer: identity-free badge + dates + Take the tour. No PII, no contact-sales pitch. */}
          {isFullDemo && user && <DemoFooter user={user} collapsed={collapsed} navigate={navigate} />}

          {/* Admin: same Take-the-tour button as demo accounts get, in the same sidebar spot,
              so the founder previews the exact entry point a prospect lands on. Only rendered
              when the sidebar is expanded — collapsed mode reserves space for primary nav. */}
          {!collapsed && user?.role === 'admin' && !isFullDemo && (
            <AdminTourLauncher navigate={navigate} />
          )}

          {/* Language Toggle */}
          <button
            data-testid="lang-toggle-btn"
            onClick={() => setLang(lang === 'en' ? 'fr' : 'en')}
            style={{
              display: 'flex', alignItems: 'center',
              justifyContent: collapsed ? 'center' : 'flex-start',
              gap: '0.75rem', width: '100%', padding: '0.625rem 1rem',
              borderRadius: '0.625rem', border: 'none', background: 'transparent',
              cursor: 'pointer', fontFamily: 'Nunito, sans-serif', fontSize: '0.85rem',
              fontWeight: 600, color: '#64748B', transition: 'all 0.15s ease',
            }}
          >
            <Globe size={18} style={{ flexShrink: 0 }} />
            {!collapsed && (
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: '0.375rem',
              }}>
                <span style={{
                  fontWeight: lang === 'en' ? 800 : 500,
                  color: lang === 'en' ? '#1E2140' : '#94A3B8',
                  transition: 'all 0.15s',
                }}>EN</span>
                <span style={{ color: '#CBD5E1' }}>/</span>
                <span style={{
                  fontWeight: lang === 'fr' ? 800 : 500,
                  color: lang === 'fr' ? '#1E2140' : '#94A3B8',
                  transition: 'all 0.15s',
                }}>FR</span>
              </span>
            )}
          </button>

          {/* Settings — only for full users (hidden for URL-demo + invite_lead guests) */}
          {!isFullDemo && (
            <NavLink
              to="/settings"
              data-testid="sidebar-nav-settings"
              style={({ isActive }) => ({
                display: 'flex', alignItems: 'center',
                justifyContent: collapsed ? 'center' : 'flex-start',
                gap: '0.75rem', width: '100%', padding: '0.625rem 1rem',
                borderRadius: '0.625rem', textDecoration: 'none',
                fontFamily: 'Nunito, sans-serif', fontSize: '0.85rem',
                fontWeight: isActive ? 700 : 500,
                color: isActive ? '#1E2140' : '#94A3B8',
                background: isActive ? '#F1F5F9' : 'transparent',
                transition: 'all 0.15s ease',
              })}
            >
              <Settings size={18} style={{ flexShrink: 0 }} />
              {!collapsed && t('nav.settings')}
            </NavLink>
          )}

          <button
            onClick={() => setCollapsed(!collapsed)}
            data-testid="sidebar-toggle"
            style={{
              display: 'flex', alignItems: 'center',
              justifyContent: collapsed ? 'center' : 'flex-start',
              gap: '0.75rem', width: '100%', padding: '0.625rem 1rem',
              borderRadius: '0.625rem', border: 'none', background: 'transparent',
              cursor: 'pointer', fontFamily: 'Nunito, sans-serif', fontSize: '0.85rem',
              color: '#94A3B8', transition: 'all 0.15s ease',
            }}
          >
            {collapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
            {!collapsed && t('nav.collapse')}
          </button>

          {!isFullDemo && (
            <button
              onClick={handleLogout}
              data-testid="sidebar-logout"
              style={{
                display: 'flex', alignItems: 'center',
                justifyContent: collapsed ? 'center' : 'flex-start',
                gap: '0.75rem', width: '100%', padding: '0.625rem 1rem',
                borderRadius: '0.625rem', border: 'none', background: 'transparent',
                cursor: 'pointer', fontFamily: 'Nunito, sans-serif', fontSize: '0.85rem',
                color: '#E03131', transition: 'all 0.15s ease',
              }}
            >
              <LogOut size={18} />
              {!collapsed && t('nav.signOut')}
            </button>
          )}
        </div>
      </aside>

      {/* Main content */}
      <main style={{ flex: 1, height: '100%', overflow: 'hidden', position: 'relative' }}>
        <MuteButton />
        <Outlet />
      </main>
    </div>
  );
}

/* Demo-visitor footer card. Identity-free: a quiet "Demo access" badge, provisioned/expires line,
   and a Take-the-tour button (since demo visitors don't have Settings). Collapsed mode shrinks to
   a single compass affordance. */
function DemoFooter({ user, collapsed, navigate }) {
  const launchTour = () => {
    navigate('/browse');
    setTimeout(() => window.dispatchEvent(new CustomEvent('frenchboard:start-tour')), 120);
  };
  const fmt = (iso) => {
    if (!iso) return null;
    try {
      return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
    } catch { return null; }
  };
  const provisioned = fmt(user.created_at);

  if (collapsed) {
    return (
      <button
        onClick={launchTour}
        data-testid="sidebar-demo-take-tour-collapsed"
        title="Take the tour"
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          width: '100%', padding: '0.625rem 0', marginBottom: '0.5rem',
          borderRadius: '0.625rem', border: 'none', background: '#F8F9FB', cursor: 'pointer',
        }}
      >
        <Compass size={18} color="#1E2140" />
      </button>
    );
  }

  return (
    <div data-testid="sidebar-demo-footer" style={{
      padding: '0.25rem 0.1rem 0.5rem',
      marginBottom: '0.5rem',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
        <span style={{
          width: 6, height: 6, borderRadius: '50%', background: '#2ECC71',
          boxShadow: '0 0 0 3px rgba(46,204,113,0.18)',
        }} />
        <span style={{
          fontFamily: 'Nunito, sans-serif', fontSize: '0.68rem',
          fontWeight: 700, color: '#475569', letterSpacing: '0.06em',
          textTransform: 'uppercase',
        }}>Demo access</span>
      </div>
      {(provisioned) && (
        <div style={{
          marginTop: '0.3rem',
          fontFamily: 'Nunito, sans-serif', fontSize: '0.7rem', color: '#94A3B8', lineHeight: 1.45,
        }}>
          Provisioned {provisioned}
        </div>
      )}
      <button
        onClick={launchTour}
        data-testid="sidebar-demo-take-tour"
        style={{
          marginTop: '0.7rem', display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
          padding: '0.45rem 0.8rem', borderRadius: '0.5rem',
          background: 'white', border: '1px solid #1E2140',
          fontFamily: 'Nunito, sans-serif', fontSize: '0.78rem', fontWeight: 700,
          color: '#1E2140', cursor: 'pointer',
          animation: 'fb-tour-pulse 2.8s ease-in-out infinite',
        }}
      >
        <Compass size={14} /> Take the tour
      </button>
      {/* Subtle, professional pulse — a soft halo, not eye-straining motion. */}
      <style>{`
        @keyframes fb-tour-pulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(30,33,64,0.18); }
          50%      { box-shadow: 0 0 0 6px rgba(30,33,64,0); }
        }
      `}</style>
    </div>
  );
}

/* Admin tour launcher: identical visual to the demo-account "Take the tour" button so the
   founder previews the exact entry point a prospect lands on, in the exact same sidebar spot.
   Title-cased "Preview tour" because admins are not prospects — keeps the demo affordance
   honest for the people it's actually for. */
function AdminTourLauncher({ navigate }) {
  const launchTour = () => {
    navigate('/browse');
    setTimeout(() => window.dispatchEvent(new CustomEvent('frenchboard:start-tour')), 120);
  };
  return (
    <div style={{ marginBottom: '0.5rem' }}>
      <button
        onClick={launchTour}
        data-testid="sidebar-admin-take-tour"
        style={{
          display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
          padding: '0.45rem 0.8rem', borderRadius: '0.5rem',
          background: 'white', border: '1px solid #1E2140',
          fontFamily: 'Nunito, sans-serif', fontSize: '0.78rem', fontWeight: 700,
          color: '#1E2140', cursor: 'pointer',
          animation: 'fb-tour-pulse 2.8s ease-in-out infinite',
        }}
      >
        <Compass size={14} /> Preview tour
      </button>
      <style>{`
        @keyframes fb-tour-pulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(30,33,64,0.18); }
          50%      { box-shadow: 0 0 0 6px rgba(30,33,64,0); }
        }
      `}</style>
    </div>
  );
}
